import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentAssignments, getAssignableStaff, assignCase, getCaseComments, addCaseComment } from '../api/admin';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';

// Permissions & History Development Plan (13.10.2026) — "Case Control ›
// Current Assignments" for a restricted account (internal staff or an
// external contractor who hasn't been released beyond the basics). Always
// shows the viewer's own current work; the open pool and other people's
// assignments only appear once the Owner releases those specific switches
// (case_view_open_pool / case_view_others) -- the server already enforces
// this, this page just reflects what came back.
const CASE_TYPE_LABELS = { damage_case: 'Damage claim', application: 'Provider application', support_ticket: 'Support ticket', verification: 'Identity verification', incident: 'Incident report' };
const STATUS_LABELS = { open: 'Open', awaiting_acceptance: 'Awaiting acceptance', accepted: 'Accepted', submitted: 'Submitted for acceptance', done: 'Done' };
const COLOR_DOTS = { normal: '#16a34a', warning: '#eab308', critical: '#f97316', escalated: '#dc2626' };

const CaseControlCurrent = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ can_view_open_pool: false, can_view_others: false, can_assign_to_others: false });
  const [assignModal, setAssignModal] = useState(null);
  const [commentsPanel, setCommentsPanel] = useState(null);

  const load = () => {
    setLoading(true);
    getCurrentAssignments().then((res) => {
      setCases(res.data?.cases || []);
      setMeta({ can_view_open_pool: !!res.data?.can_view_open_pool, can_view_others: !!res.data?.can_view_others, can_assign_to_others: !!res.data?.can_assign_to_others });
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAssign = async (c) => {
    setAssignModal({ caseType: c.case_type, caseId: c.case_id, candidates: [], loading: true, error: null, message: '' });
    try {
      const res = await getAssignableStaff(c.case_type, c.case_id);
      setAssignModal((m) => ({ ...m, candidates: res.data?.candidates || [], loading: false }));
    } catch (err) {
      setAssignModal((m) => ({ ...m, loading: false, error: err.response?.data?.error || 'Failed to load candidates' }));
    }
  };
  const confirmAssign = async (adminId) => {
    try {
      await assignCase(assignModal.caseType, assignModal.caseId, adminId, assignModal.message);
      setAssignModal(null);
      load();
    } catch (err) {
      setAssignModal((m) => ({ ...m, error: err.response?.data?.error || 'Assignment failed' }));
    }
  };

  const openComments = (c) => {
    setCommentsPanel({ caseType: c.case_type, caseId: c.case_id, displayId: c.display_id, comments: [], draft: '', loading: true });
    getCaseComments(c.case_type, c.case_id).then((res) => setCommentsPanel((p) => (p ? { ...p, comments: res.data?.comments || [], loading: false } : p)));
  };
  const submitComment = async () => {
    if (!commentsPanel.draft.trim()) return;
    const res = await addCaseComment(commentsPanel.caseType, commentsPanel.caseId, commentsPanel.draft);
    setCommentsPanel((p) => ({ ...p, comments: [...p.comments, res.data.comment], draft: '' }));
  };

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>{t('Case Management')} › {t('My Current Cases')}</div>
      <h1 style={s.h1}>{t('My Current Cases')}</h1>
      <p style={s.hint}>
        {t('Your own assigned cases.')}
        {meta.can_view_open_pool && ' You can also see the open, unassigned pool.'}
        {meta.can_view_others && ' You can also see other people’s current assignments.'}
        {!meta.can_view_open_pool && !meta.can_view_others && ' Ask the Owner to release more visibility under Team & Roles if you need to see more.'}
      </p>

      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}></th><th style={s.th}>{t('Country')}</th><th style={s.th}>{t('Case')}</th><th style={s.th}>{t('Type')}</th>
            <th style={s.th}>{t('Status')}</th><th style={s.th}>{t('Assigned to')}</th><th style={s.th}>{t('Actions')}</th>
          </tr></thead>
          <tbody>
            {cases.map((c) => (
              <tr key={`${c.case_type}:${c.case_id}`}>
                <td style={s.td} title={c.color}><span style={{ ...s.colorDot, background: COLOR_DOTS[c.color] || '#d1d5db' }} /></td>
                <td style={s.td}>{c.country_code || '—'}</td>
                <td style={s.td}>{c.display_id} <span style={s.titleSmall}>{c.title}</span></td>
                <td style={s.td}>{CASE_TYPE_LABELS[c.case_type]}</td>
                <td style={s.td}><span style={s.badge}>{t(STATUS_LABELS[c.case_control_status] || c.case_control_status)}</span></td>
                <td style={s.td}>{c.assignment ? (c.assignment.assigned_to_admin_id === user?.id ? t('You') : t('Assigned')) : <span style={s.muted}>{t('Open')}</span>}</td>
                <td style={s.td}>
                  {c.case_type === 'support_ticket' && <Link style={{ ...s.btnGray, textDecoration: 'none', display: 'inline-block' }} to={`/case-control/support/${c.case_id}`}>{t('Open case')}</Link>}
                  {c.assignment && <button style={s.btnGray} onClick={() => openComments(c)}>{t('Comments')}</button>}
                  {meta.can_assign_to_others && !c.assignment && <button style={s.btnGreen} onClick={() => openAssign(c)}>{t('Assign')}</button>}
                  {c.assignment?.order_document_id && <span style={s.titleSmall}>{t('Order Document')} ready</span>}
                </td>
              </tr>
            ))}
            {cases.length === 0 && <tr><td style={s.td} colSpan={7}>{t('No current assignments.')}</td></tr>}
          </tbody>
        </table>
      )}

      {assignModal && (
        <div style={s.modalBackdrop} onClick={() => setAssignModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('Assign case')}</h3>
            {assignModal.loading ? <p>Loading candidates…</p> : (
              <>
                {assignModal.error && <div style={s.errorBox}>{assignModal.error}</div>}
                <textarea style={s.textarea} placeholder="Task message (optional)" value={assignModal.message} onChange={(e) => setAssignModal((m) => ({ ...m, message: e.target.value }))} />
                {assignModal.candidates.map((cand) => (
                  <div key={cand.admin_id} style={{ ...s.candidateRow, opacity: cand.assignable ? 1 : 0.5 }}>
                    <div>
                      <strong>{cand.full_name || cand.email}</strong>
                      {!cand.assignable && <div style={s.reasonText}>✗ {cand.reason}</div>}
                    </div>
                    <button style={s.btnGreen} disabled={!cand.assignable} onClick={() => confirmAssign(cand.admin_id)}>Send task</button>
                  </div>
                ))}
              </>
            )}
            <button style={s.btnGray} onClick={() => setAssignModal(null)}>Close</button>
          </div>
        </div>
      )}

      {commentsPanel && (
        <div style={s.modalBackdrop} onClick={() => setCommentsPanel(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Comments — {commentsPanel.displayId}</h3>
            {commentsPanel.loading ? <p>Loading…</p> : (
              <div style={s.commentList}>
                {commentsPanel.comments.map((c) => (
                  <div key={c.id} style={s.commentRow}>
                    <div style={s.commentMeta}><strong>{c.author?.full_name || c.author?.email || 'Unknown'}</strong> · {new Date(c.created_at).toLocaleString()}</div>
                    <div>{c.content}</div>
                  </div>
                ))}
                {commentsPanel.comments.length === 0 && <p style={s.empty}>No comments yet.</p>}
              </div>
            )}
            <textarea style={s.textarea} placeholder="Add a comment" value={commentsPanel.draft} onChange={(e) => setCommentsPanel((p) => ({ ...p, draft: e.target.value }))} />
            <button style={s.btnGreen} disabled={!commentsPanel.draft.trim()} onClick={submitComment}>Add comment</button>
            <button style={s.btnGray} onClick={() => setCommentsPanel(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1100 },
  breadcrumb: { fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 16, maxWidth: 700 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  titleSmall: { color: '#9ca3af', fontSize: 11, display: 'block' },
  muted: { color: '#9ca3af', fontSize: 12 },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700 },
  colorDot: { display: 'inline-block', width: 12, height: 12, borderRadius: '50%' },
  btnGreen: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 },
  modal: { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
  candidateRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  reasonText: { color: '#dc2626', fontSize: 11.5 },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 8, borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap' },
  textarea: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minHeight: 50, resize: 'vertical' },
  commentList: { maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  commentRow: { background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: 8, fontSize: 13 },
  commentMeta: { fontSize: 11.5, color: '#9ca3af', marginBottom: 2 },
  empty: { color: '#9ca3af', fontSize: 13 },
};

export default CaseControlCurrent;
